/* eslint-disable react/no-unescaped-entities */
"use client"

import FormError from "@/components/FormError"
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui-pro/responsive-modal"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  ConfigurableProduct,
  ConfigurableProductOptions,
  ConfigurableVariant,
  Maybe,
} from "@/types/graphql.prod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import * as z from "zod"
import { useAddProductTip } from "./use-notify-me-form-dialog-new"
import classes from "./new-notify-me-form-dialog.module.css"

export const NewNotifyMeFormDialog = ({
  isOpen,
  setNotifyMeFormDialogIsOpen,
  product,
  variants,
  configurable_options,
  optionSelections,
}: {
  isOpen: boolean
  setNotifyMeFormDialogIsOpen: (open: boolean) => void
  optionSelections: Map<any, any>
  variants: Maybe<Maybe<ConfigurableVariant>[]> | undefined
  configurable_options: Maybe<Maybe<ConfigurableProductOptions>[]> | undefined
  product: ConfigurableProduct
}) => {
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)

  const { isLoading, response, formErrors, handleSubmit } = useAddProductTip({
    product,
  })

  useEffect(() => {
    if (response && response.code === "200") {
      setShowSuccessDialog(true)
      setNotifyMeFormDialogIsOpen(false)
    }
  }, [response, setNotifyMeFormDialogIsOpen, setShowSuccessDialog])

  const selectedProduct = variants?.find((temp) => {
    const attributes = temp?.attributes
    let flag = true

    attributes?.forEach((attr) => {
      const code = attr?.code
      const value_index = attr?.value_index
      const attribute_id = configurable_options?.find(
        (temp) => temp?.attribute_code === code
      )?.attribute_id

      if (optionSelections.get(attribute_id) !== value_index) {
        flag = false
      }
    })

    return flag
  })

  const findSelectedLabel = ({
    selectedProduct,
    configurable_options,
    attribute_code,
  }: {
    selectedProduct: any
    configurable_options: any
    attribute_code: string
  }): string | undefined => {
    // 获取selectedProduct中的color属性
    const colorAttribute = selectedProduct?.attributes?.find(
      (attr: any) => attr.code === attribute_code
    )

    if (!colorAttribute) {
      return undefined
    }

    // 在configurable_options中找到color选项
    const colorOption = configurable_options.find(
      (option: any) => option.attribute_code === attribute_code
    )

    if (!colorOption) {
      return undefined
    }

    // 在values中找到匹配的value_index
    const selectedValue = colorOption.values.find(
      (value: any) => value.value_index === colorAttribute.value_index
    )

    return selectedValue?.default_label
  }

  const getDefaultValues = () => {
    const values: Record<string, any> = {
      email: "",
      agree: false,
    }

    configurable_options?.forEach((item) => {
      if (item?.attribute_code) {
        const label = findSelectedLabel({
          configurable_options,
          selectedProduct,
          attribute_code: item?.attribute_code,
        })

        values[item?.attribute_code] = label
      }
    })

    return values
  }

  const defaultValues = getDefaultValues()

  const createFormSchema = (
    options: Maybe<Maybe<ConfigurableProductOptions>[]> | undefined
  ) => {
    const schemaFields: Record<string, z.ZodTypeAny> = {}

    // schemaFields["agree"] = z.boolean().refine((val) => val === true, {
    //   message: "This is a required field",
    // })
    schemaFields["email"] = z
      .string()
      .email("Please enter a valid email address")

    if (!options) {
      return z.object(schemaFields)
    }

    options?.forEach((item) => {
      if (item?.attribute_code) {
        schemaFields[item?.attribute_code] = z.string({
          message: `This is a required field!`,
        })
      }
    })

    return z.object(schemaFields)
  }

  const formSchema = createFormSchema(configurable_options)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
  })

  const data = form.watch()

  useEffect(() => {
    if (configurable_options && selectedProduct) {
      form.reset(getDefaultValues())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurable_options, selectedProduct, optionSelections])

  function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      handleSubmit({
        email: values?.email,
        selectedProduct: selectedProduct,
      })
    } catch (error) {
      console.error("Form submission error", error)
      toast.error("Failed to submit the form. Please try again.")
    }
  }

  return (
    <div>
      <ResponsiveModal
        onOpenChange={setShowSuccessDialog}
        open={showSuccessDialog}
      >
        {/* @ts-expect-error - Explain WHY you're ignoring this error */}
        <ResponsiveModalContent
          side="bottom"
          className="flex  flex-col items-center justify-center rounded-b-none rounded-t-[20px] lg_max-w-lg lg_rounded-[20px]"
        >
          {/* @ts-expect-error - Explain WHY you're ignoring this error */}
          <ResponsiveModalHeader>
            <div className="text-center font-poppins text-[32px] font-[600] text-primary lg_text-[34px]">
              Thank You!
            </div>

            <div className="text-center font-sans text-[18px] text-primary lg_text-lg">
              We&apos;ll give you a shout as soon as the{" "}
              <span className="font-bold">{product.name}</span> is In Stock.
            </div>

            <div className="mt-[20px] flex justify-center">
              <Button
                className="mt-[20px] h-[54px] w-full rounded-[10px] bg-primary font-poppins font-[600] uppercase text-white"
                onClick={() => setShowSuccessDialog(false)}
              >
                Continue Shopping
              </Button>
            </div>
          </ResponsiveModalHeader>
        </ResponsiveModalContent>
      </ResponsiveModal>

      <ResponsiveModal open={isOpen} onOpenChange={setNotifyMeFormDialogIsOpen}>
        {/* @ts-expect-error - Explain WHY you're ignoring this error */}
        <ResponsiveModalContent
          side="bottom"
          className="rounded-b-none rounded-t-[20px] px-0 pb-0 lg_max-w-[1200px] lg_rounded-[20px] lg_p-10"
        >
          <div className="px-[17px] lg_px-0">
            <div className="flex items-center justify-between border-b-1 border-[#ddd] pb-[20px] font-poppins text-[16px] font-[600] leading-[1.2] text-title lg_text-[18px] lg_leading-[1.4]">
              <span> Out of stock Notification</span>
              <button
                onClick={() => {
                  setNotifyMeFormDialogIsOpen(false)
                }}
                type="button"
                className="flex h-5 w-5 items-center justify-center lg_h-7 lg_w-7"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="17"
                  fill="none"
                  viewBox="0 0 18 17"
                >
                  <path
                    stroke="#666"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M16.5 16 1.5 1m15 0-15 15"
                  ></path>
                </svg>
              </button>
            </div>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="relative mx-auto w-full  px-[17px] pb-[122px] pt-[20px] lg_px-0 lg_pb-0 lg_pt-0"
            >
              <div className="grid grid-cols-12 gap-4">
                <FormError errors={Array.from(formErrors.values())} />

                {configurable_options?.map((item) => {
                  if (item?.attribute_code) {
                    return (
                      <div
                        className="col-span-12 lg_col-span-6"
                        key={item.attribute_code}
                      >
                        <FormField
                          control={form.control}
                          name={item?.attribute_code}
                          render={({ field }) => {
                            return (
                              <FormItem className="space-y-2.5 lg_space-y-[20px]">
                                <FormLabel className="font-poppins font-[600] text-title">
                                  {item?.label}
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder=""
                                    className="h-10 rounded-[10px] border-[#adadad] bg-[#EFEFEF] text-[#ADADAD] focus_border-[#EFEFEF] lg_h-[54px]"
                                    type="text"
                                    readOnly
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )
                          }}
                        />
                      </div>
                    )
                  }
                })}

                <div className="col-span-12 lg_col-span-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="space-y-2.5 lg_space-y-[20px]">
                        <FormLabel className="font-poppins font-[600] text-title">
                          Your Email*
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter your email to get notified"
                            className="h-10 rounded-[10px] border-[#adadad] lg_h-[54px]"
                            type="email"
                            {...field}
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <FormField
                control={form.control}
                name="agree"
                render={({ field }) => (
                  <FormItem className="mt-5 flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-sans leading-[1.4] text-title">
                        Subscribe our newsletter for exclusive offers.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
              <div className="absolute bottom-0 left-0 w-full px-[18px] py-[17px] shadow-[0_-4px_4px_0_rgba(0,0,0,0.1)] lg_static lg_left-auto lg_top-auto lg_mt-10 lg_p-0 lg_shadow-[none]">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className=" h-12 w-full rounded-[10px] bg-primary font-poppins font-[600] uppercase text-white lg_h-[54px]"
                >
                  Submit
                </Button>
              </div>

              <div className="mt-[20px] font-sans leading-[1.4] text-title">
                By clicking "Submit," you authorize KingsYard to send you a
                one-time email notification when this product becomes available.
              </div>
            </form>
          </Form>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  )
}
